import type { Point, RigDef } from '../../game/rigManifest'
import { hipHeight } from '../../game/rigPose'
import type { RigPose } from '../../game/rigPose'

/**
 * The cutout rig laid out for the DOM (PHASE3 T3.1, D-048): the same placement as the race's
 * Pixi `RunnerRig` (hip on the lowest leg, torso around the hip, head on the neck, arms on the
 * shoulder, back limbs shaded), as absolutely positioned images turned around their pivots.
 * Coordinates are image pixels with the feet centre at (0, 0) and up negative; the component
 * scales the whole rig. No Pixi, so the hub stays in the small menu bundle.
 */

/** Must match `RunnerRig.ts`: legs sit this share of the torso width apart at the hip. */
const HIP_SPREAD = 0.12

export type PieceKey = 'armB' | 'legB' | 'tail' | 'legF' | 'torso' | 'head' | 'armF'

/** Natural sizes of the images a rig needs for its layout. */
export interface RigSizes {
  torsoW: number
  legH: number
}

export interface PiecePlacement {
  key: PieceKey
  src: string
  /** The image point placed at (`x`, `y`) and turned around. */
  pivot: Point
  x: number
  y: number
  /** Radians, clockwise positive (CSS and Pixi agree). */
  rot: number
  /** Back arm and leg: drawn darker. */
  back: boolean
}

export interface RigLayout {
  /** Back to front. */
  pieces: PiecePlacement[]
  /** Top of the head (negative, image px above the feet). */
  headTop: number
}

function rotate([x, y]: Point, a: number): Point {
  const c = Math.cos(a)
  const s = Math.sin(a)
  return [x * c - y * s, x * s + y * c]
}

/** Every image the preview loads for `rig` (calm face only). */
export function rigImages(rig: RigDef): string[] {
  const out = [rig.faces.calm, rig.torso.src, rig.arm.src, rig.leg.src]
  if (rig.tail) out.push(rig.tail.src)
  return out
}

/** Lay out `rig` in `pose` (calm face): the DOM twin of `RunnerRig.update`. */
export function placeRig(rig: RigDef, pose: RigPose, sizes: RigSizes): RigLayout {
  const px = rig.px
  const legLen = sizes.legH - rig.leg.pivot[1]
  const hip: Point = [0, -hipHeight(pose, legLen / px) * px]
  const t = rig.torso
  const rel = (p: Point): Point => {
    const [x, y] = rotate([p[0] - t.pivot[0], p[1] - t.pivot[1]], pose.body)
    return [hip[0] + x, hip[1] + y]
  }
  const spread = sizes.torsoW * HIP_SPREAD
  const neck = rel(t.neck)
  const sh = rel(t.shoulder)
  const at = (
    key: PieceKey,
    src: string,
    pivot: Point,
    [x, y]: Point,
    rot: number,
    back = false,
  ): PiecePlacement => ({ key, src, pivot, x, y, rot, back })

  const pieces: PiecePlacement[] = [
    at(
      'armB',
      rig.arm.src,
      rig.arm.pivot,
      [sh[0] - spread / 3, sh[1]],
      pose.body - pose.armB,
      true,
    ),
    at('legB', rig.leg.src, rig.leg.pivot, [hip[0] - spread / 2, hip[1]], -pose.legB, true),
  ]
  if (rig.tail && t.tailRoot) {
    const tailRot = pose.body + 0.2 * Math.sin(pose.lift)
    pieces.push(at('tail', rig.tail.src, rig.tail.pivot, rel(t.tailRoot), tailRot))
  }
  pieces.push(
    at('legF', rig.leg.src, rig.leg.pivot, [hip[0] + spread / 2, hip[1]], -pose.legF),
    at('torso', t.src, t.pivot, hip, pose.body),
    at('head', rig.faces.calm, rig.headPivot, neck, pose.body + pose.head),
    at('armF', rig.arm.src, rig.arm.pivot, sh, pose.body - pose.armF),
  )
  return { pieces, headTop: neck[1] - rig.headPivot[1] }
}

const r2 = (n: number): string => (Math.round(n * 100) / 100).toFixed(2)

/** Inline CSS for one piece: the pivot lands on (`x`, `y`) and the image turns around it. */
export function pieceStyle(p: PiecePlacement): {
  left: string
  top: string
  transformOrigin: string
  transform: string
} {
  return {
    left: `${r2(p.x - p.pivot[0])}px`,
    top: `${r2(p.y - p.pivot[1])}px`,
    transformOrigin: `${r2(p.pivot[0])}px ${r2(p.pivot[1])}px`,
    transform: `rotate(${(Math.round(p.rot * 10000) / 10000).toFixed(4)}rad)`,
  }
}

/** What the preview draws: the rig, a single image, or the coloured placeholder box. */
export type PreviewMode = 'rig' | 'image' | 'box'

export function previewMode(
  slot: { rig: RigDef | null; image: string | null },
  failed: boolean,
): PreviewMode {
  if (failed) return 'box'
  if (slot.rig) return 'rig'
  return slot.image ? 'image' : 'box'
}

/** `public/assets/<src>` under the app's base path (as `sprites.ts` builds it). */
export function assetUrl(src: string): string {
  return `${import.meta.env.BASE_URL}assets/${src}`
}
